import type { AnchorHTMLAttributes, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'accent' | 'outline' | 'light' | 'outline-light'
export type ButtonSize = 'md' | 'lg'

interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
    variant?: ButtonVariant
    size?: ButtonSize
}

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-ink text-paper shadow-[0_12px_24px_-14px_rgb(23_34_56/0.8)] hover:bg-ink-soft',
    accent: 'bg-coral-deep text-white shadow-[0_12px_24px_-14px_rgb(181_71_36/0.9)] hover:bg-[#9c3b1c]',
    outline: 'border border-ink/15 bg-white/70 text-ink hover:border-ink/30 hover:bg-white',
    light: 'bg-paper text-ink hover:bg-white',
    'outline-light': 'border border-white/25 text-paper hover:border-white/50 hover:bg-white/10'
}

const SIZES: Record<ButtonSize, string> = {
    md: 'min-h-11 px-5 text-[0.9375rem]',
    lg: 'min-h-13 px-6 text-base'
}

export const buttonClasses = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string => {
    return cn(
        'inline-flex items-center justify-center gap-2 rounded-full text-center font-semibold transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2 focus-visible:ring-offset-paper disabled:pointer-events-none disabled:opacity-60',
        VARIANTS[variant],
        SIZES[size],
        className
    )
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    variant,
    size,
    className,
    children,
    ...props
}): ReactNode => {
    return (
        <a className={buttonClasses(variant, size, className)} {...props}>
            {children}
        </a>
    )
}
